import { FC, useMemo, useState } from 'react'
import moment from 'moment'
import { toast } from 'react-toastify'
import { useParams } from 'react-router-dom'
import { ReactComponent as AnalyzingIcon } from 'src/assets/svg/analyzing-48.svg'
import { ReactComponent as EmptyComparisonIcon } from 'src/assets/svg/empty-comparison-48.svg'
import { ReactComponent as CalendarIcon } from 'src/assets/svg/calendar-line-16.svg'
import { ReactComponent as ExportIcon } from 'src/assets/svg/blue-file-download.svg'
import { ReactComponent as WarningTriangleIcon } from 'src/assets/svg/warning-triangle-48.svg'
import { cn } from 'src/utils/cn'
import { ReactComponent as ComparisonReportIcon } from 'src/assets/svg/comparison-report-64.svg'
import { ComparisonHistoryPanel } from 'src/components/ComparisonHistoryPanel'
import { Report } from 'src/charts/YourReports/interfaces/report.interface'
import {
  useGetReviewComparisonFacilitiesQuery,
  useGetReviewComparisonQuery,
  useGetReviewComparisonsQuery,
  useReviewComparisonMutations,
} from 'src/reactQuery/useReviewComparison'
import { ReviewAnalysisApiService } from 'src/services/reviewAnalysisApi'
import {
  ReviewComparisonResult,
  ReviewComparisonSummary,
} from 'src/services/reviewAnalysisApi/interfaces/reviewComparison.interface'
import styles from './reviewComparison.module.scss'
import { NewReviewComparisonModal } from './NewReviewComparisonModal'
import { PlatformPerformance } from './PlatformPerformance'
import { RatingTrend } from './RatingTrend'
import { ReviewKeyMetrics } from './ReviewKeyMetrics'
import { SentimentDistribution } from './SentimentDistribution'
import { SentimentFeed } from './SentimentFeed'
import { TopicComparison } from './TopicComparison'
import { ReviewHeaderOptions } from '../ReviewAnalysisHeader'

interface ReviewComparisonProps {
  // The page's header, rendered by this view so each screen can name its own
  // way back: the list returns to the overview, a report returns to the list.
  header: (options: ReviewHeaderOptions) => JSX.Element
  // Leaves the comparison for the general overview.
  onExitComparison: () => void
  // The comparison the user is building, once the modal is wired up.
  onStart?: () => void
}

/*
 * Review Analysis → Reporting & Comparison.
 *
 * Facilities and platforms compared over one window, the way Social Listening
 * compares campaigns. The states are deliberately the ones that feature already
 * uses: a user who has generated one comparison should recognise the other.
 *
 * Every figure comes from the backend, computed on the read that fetches it —
 * there is nothing to poll and nothing cached, so a saved comparison reflects
 * the reviews as they stand rather than as they stood when it was made.
 */
const COPY = {
  compareLongerPeriods: 'Compare Longer Periods',

  deleteFailed: 'We could not delete this comparison. Please try again.',

  downloadFailed: 'We could not prepare this comparison. Please try again.',
  // Two lines, breaking after "to", as the mock reads it. Written as the two
  // lines rather than left to a max-width: a width that happens to break here
  // today breaks somewhere else the moment the copy or the font changes.
  emptyStateSubtitle: [
    'Choose your date range and select a comparison type to',
    'evaluate performance.',
  ],
  emptyStateTitle: 'Start Comparing Your Data',
  exportComparison: 'Export Comparison',
  exportingComparison: 'Preparing PDF…',
  failedStateSubtitle:
    'An unexpected error occurred while generating your comparison report. ' +
    'Please try again. If the issue persists, contact support.',
  failedStateTitle: 'Analysis Failed',
  generateAgain: 'Generate Again',
  generatingSubtitle:
    "We're analyzing your selected data to generate meaningful comparison " +
    'insights. This may take a few hours depending on the amount of data ' +
    'being processed.',
  generatingTitle: 'Analyzing Your Data & Generating Comparison Report',
  // What the list is a list of, and what making another one is called.
  listAdd: 'Add New Comparison',

  listDeleteTarget: 'this comparison',

  listPill: 'facility - facility',

  listSubtitle: 'Build a comparative report between facilities and platforms.',

  listTitle: 'Reporting & Comparison',

  // Distinct from a short period: the window may be long and the facilities
  // real, and one of them still collected no reviews in it.
  noDataSubtitle:
    'At least one of the selected facilities has no reviews in this period, ' +
    'so every widget would be measured against an empty column. Try a longer ' +
    'period, or facilities that were collecting reviews during it.',

  noDataTitle: 'Not Enough Data to Compare',
  reportFailed:
    'We could not compute this comparison. It may no longer have two ' +
    'facilities with reviews.',
  reportSubtitle:
    'See how two facilities stack up across ratings, sentiment, platforms and ' +
    'guest feedback.',
  reportTitle: 'Compare facilities',
  shortPeriodSubtitle:
    'The selected date ranges are too short to generate reliable comparison ' +
    'insights. For more meaningful trends and recommendations, compare longer ' +
    'periods such as a month or a quarter.',
  shortPeriodTitle: 'Short Comparison Period',
  startComparison: 'Start Comparison',
  // What the trail names on a report — the list it came from, called what the
  // switch above it calls that view.
  trail: 'Comparisons',
}

/*
 * A comparison needs two sides. A facility with no reviews in the window fills
 * nothing on its half of the report — no arc, no trend, no topic, no row — so
 * every widget reads against an empty column. A report of zeroes looks like an
 * answer and isn't one, which is what the tester hit on Social Listening.
 *
 * The sentiment split is the count itself: its three bands are every review
 * the facility has in the window.
 */
const isComparable = (result: ReviewComparisonResult) =>
  result.sentimentDistribution.length > 0 &&
  result.sentimentDistribution.every(
    (split) => split.negative + split.neutral + split.positive > 0
  )

// Which screen the view is showing. Client-held until the endpoints exist, at
// which point this becomes the stored comparison's own state.
export type ReviewComparisonStage =
  | 'failed'
  | 'generating'
  | 'list'
  | 'report'
  | 'setup'
  | 'shortPeriod'

// What the rows' own states are called on a card, which is where the list's
// copy for them lives.
const ROW_COPY = {
  failedSubtext:
    'We couldn’t generate this comparison due to a temporary issue.',
  failedTitle: (report: { name: string }) => report.name,
  generating: 'Generating comparison…',
  retry: 'Retry Generate Comparison',
}

export const ReviewComparison: FC<ReviewComparisonProps> = ({
  header,
  onExitComparison,
  onStart,
}) => {
  const { campaignId = '' } = useParams<{ campaignId: string }>()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  // Which comparison is open, if any. The list is the view when nothing is.
  const [openedId, setOpenedId] = useState<string | null>(null)

  const { data: comparisons = [], isLoading: isListLoading } =
    useGetReviewComparisonsQuery(campaignId)
  const { data: pickerFacilities = [] } =
    useGetReviewComparisonFacilitiesQuery(campaignId)
  const {
    data: opened,
    isError: isReportError,
    isLoading: isReportLoading,
  } = useGetReviewComparisonQuery(campaignId, openedId || undefined)
  const { createComparison, deleteComparison } =
    useReviewComparisonMutations(campaignId)

  const startComparison = () => {
    setIsModalOpen(true)
    onStart?.()
  }

  const facilityNames = useMemo(
    () => new Map(pickerFacilities.map((f) => [f.facilityId, f.facilityName])),
    [pickerFacilities]
  )

  // A saved comparison in the shape the shared list card reads. There is no
  // status to map: the report is computed when a row is opened, so every row
  // is openable and none is ever in flight.
  const toReport = (comparison: ReviewComparisonSummary): Report => ({
    dateRange: '',
    generatedAt: moment(comparison.createdAt).format('MMM D, YYYY'),
    id: comparison.id,
    modules: [],
    name: comparison.facilities
      .map((id) => facilityNames.get(id) || id)
      .join(' — '),
    status: 'ready',
  })

  const download = async (comparisonId: string) => {
    setIsDownloading(true)

    try {
      await ReviewAnalysisApiService.downloadComparison(
        campaignId,
        comparisonId,
        'review-comparison.pdf'
      )
    } catch {
      toast.error(COPY.downloadFailed)
    } finally {
      setIsDownloading(false)
    }
  }

  const modal = (
    <NewReviewComparisonModal
      facilities={pickerFacilities.map((facility) => facility.facilityName)}
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      onGenerate={(selected, window, quickCompare) => {
        const ids = selected
          .map(
            (name) =>
              pickerFacilities.find((f) => f.facilityName === name)?.facilityId
          )
          .filter(Boolean) as string[]

        createComparison.mutate(
          { facilities: ids, quickCompare, window },
          {
            onSuccess: (created) => {
              setIsModalOpen(false)
              // Straight into the report it just saved, which is what the
              // button said it would do.
              setOpenedId(created.id)
            },
          }
        )
      }}
    />
  )

  // The four states share a card, an icon, a heading, a sentence and at most
  // one button, so they are one function rather than four copies of it.
  const stateCard = (
    icon: JSX.Element,
    title: string,
    subtitle: string | string[],
    subtitleClassName: string,
    action?: { label: string; onClick: () => void }
  ) => (
    <div className={styles.card}>
      {header({ embedded: true })}

      <div className={styles.emptyState}>
        {icon}
        <h3 className={styles.emptyStateTitle}>{title}</h3>
        <p className={subtitleClassName}>
          {/* An array is copy that breaks where the mock breaks it, a string is
              copy that wraps wherever it lands. */}
          {Array.isArray(subtitle)
            ? subtitle.map((line) => (
                <span className={styles.subtitleLine} key={line}>
                  {line}
                </span>
              ))
            : subtitle}
        </p>

        {action && (
          <button
            className={styles.startButton}
            onClick={action.onClick}
            type="button"
          >
            {action.label}
          </button>
        )}
      </div>

      {modal}
    </div>
  )

  // The list of saved comparisons. The panel is Social Listening's — the two
  // features list the same states of the same kind of run — so what is left
  // here is naming the rows and saying what happens to them.
  if (!openedId && (isListLoading || comparisons.length > 0)) {
    return (
      <>
        {header({ onBack: onExitComparison })}

        <div className={cn(styles.card, styles.cardList)}>
          <ComparisonHistoryPanel
            addLabel={COPY.listAdd}
            deleteTarget={COPY.listDeleteTarget}
            icon={ComparisonReportIcon}
            isLoading={isListLoading}
            onAdd={() => setIsModalOpen(true)}
            onDelete={(id) =>
              deleteComparison.mutate(id, {
                onError: () => toast.error(COPY.deleteFailed),
              })
            }
            onDownload={download}
            onOpen={setOpenedId}
            // A saved definition has no run left to fail, so the panel's retry
            // branch never renders.
            onRetry={() => undefined}
            pillLabel={COPY.listPill}
            rowCopy={ROW_COPY}
            rows={comparisons.map(toReport)}
            subtitle={COPY.listSubtitle}
            title={COPY.listTitle}
          />
        </div>

        {modal}
      </>
    )
  }

  // The report. Its sections read the same left-to-right order as the
  // comparison itself: the totals, then where those totals come from.
  if (openedId && opened && !isComparable(opened.result)) {
    return stateCard(
      <WarningTriangleIcon
        className={cn(styles.warningIcon, styles.warningIconShortPeriod)}
      />,
      COPY.noDataTitle,
      COPY.noDataSubtitle,
      styles.shortPeriodSubtitle,
      {
        label: COPY.compareLongerPeriods,
        onClick: () => {
          setOpenedId(null)
          startComparison()
        },
      }
    )
  }

  if (openedId && opened) {
    const { result } = opened
    const [positiveInsight, negativeInsight] = [result.insight, result.insight]

    return (
      <div className={styles.report}>
        {header({ onBack: () => setOpenedId(null), trail: COPY.trail })}

        <div className={styles.reportTitleRow}>
          <div className={styles.reportTitleText}>
            <h2 className={styles.reportTitle}>{COPY.reportTitle}</h2>
            <p className={styles.reportSubtitle}>{COPY.reportSubtitle}</p>
          </div>

          <div className={styles.reportActions}>
            <span className={styles.reportPeriod}>
              <CalendarIcon className={styles.reportPeriodIcon} />
              {/* Only when the window came from a shortcut; a hand-picked
                  range has no name but its own dates. */}
              {opened.quickCompare && (
                <span className={styles.reportPeriodLabel}>
                  {opened.quickCompare}
                </span>
              )}
              <span className={styles.reportPeriodDates}>
                {result.periodLabel}
              </span>
            </span>

            <button
              className={styles.exportButton}
              disabled={isDownloading}
              onClick={() => download(openedId)}
              type="button"
            >
              <ExportIcon className={styles.exportIcon} />
              {isDownloading ? COPY.exportingComparison : COPY.exportComparison}
            </button>
          </div>
        </div>

        <div className={styles.reportCard}>
          <ReviewKeyMetrics
            facilities={result.facilities}
            insight={result.insight}
            metrics={result.keyMetrics}
          />
        </div>

        <div className={styles.reportCard}>
          <PlatformPerformance facilities={result.platformPerformance} />
        </div>

        <div className={styles.reportCard}>
          <RatingTrend
            period={result.periodLabel}
            series={result.ratingTrend.series}
            weeks={result.ratingTrend.weeks}
          />
        </div>

        <div className={styles.reportCard}>
          <TopicComparison
            facilities={result.facilities}
            insight={positiveInsight}
            platforms={result.topics.platforms}
            topics={result.topics.positive}
            variant="positive"
          />
        </div>

        <div className={styles.reportCard}>
          <TopicComparison
            facilities={result.facilities}
            insight={negativeInsight}
            platforms={result.topics.platforms}
            topics={result.topics.negative}
            variant="negative"
          />
        </div>

        <div className={styles.reportCard}>
          <SentimentDistribution splits={result.sentimentDistribution} />
        </div>

        <div className={styles.reportCard}>
          <SentimentFeed
            facilities={result.facilities.map(
              (facility) => facility.facilityId
            )}
            rows={result.feed}
          />
        </div>

        {modal}
      </div>
    )
  }

  // A report is computed on the read that fetches it, which takes as long as
  // it takes: this is that read, not a job being waited on.
  if (openedId && isReportLoading) {
    return (
      <div className={styles.card}>
        {header({ embedded: true })}

        <div className={styles.emptyState}>
          <AnalyzingIcon className={styles.generatingIcon} />
          <h3 className={cn(styles.emptyStateTitle, styles.generatingTitle)}>
            {COPY.generatingTitle}
          </h3>
          <p className={styles.generatingSubtitle}>{COPY.generatingSubtitle}</p>
        </div>
      </div>
    )
  }

  // The read failed, or the comparison has lost a facility and is no longer
  // one — the server answers 409 for that. Either way there is no report.
  if (openedId && isReportError) {
    return stateCard(
      <WarningTriangleIcon
        className={cn(styles.warningIcon, styles.warningIconFailed)}
      />,
      COPY.failedStateTitle,
      COPY.reportFailed,
      styles.failedStateSubtitle,
      {
        label: COPY.generateAgain,
        onClick: () => {
          setOpenedId(null)
          startComparison()
        },
      }
    )
  }

  return stateCard(
    <EmptyComparisonIcon className={styles.emptyStateIcon} />,
    COPY.emptyStateTitle,
    COPY.emptyStateSubtitle,
    styles.emptyStateSubtitle,
    { label: COPY.startComparison, onClick: startComparison }
  )
}
